import qs from 'querystring';
import isEmpty from 'lodash.isempty';
import set from 'lodash.set';
import {
  replace,
  push,
} from 'connected-react-router';
import {
  all,
  call,
  cancel,
  put,
  race,
  select,
  take,
  takeEvery,
} from 'redux-saga/effects';
import { reportActionToAhoy } from '../actions/ahoy';
import {
  addToIndex,
  forceLastPage,
  setIndexError,
  setIndexLoadingState,
} from '../actions/api-indexes';
import {
  getFacets,
  search,
  setSearchFacetsIndex,
  setSearchLoadedState,
  trackSearch,
  PERFORM_SEARCH,
  REQUEST_NEXT_SEARCH_PAGE,
  RUN_SEARCH_BY_FILTER,
  PULLUP_NOT_FOUND_INFORMATION,
} from '../actions/search';
import ApiResource from '../api-resources';
import { VIEWING_SEARCH_RESULTS_CONTEXT } from '../context';
import Routes from '../routes';
import {
  urlSearchSelector,
  urlSearchFromState,
} from '../selectors/router';
import {
  currentPageFromSearchIndexSelector,
  searchUrlHashSelector,
  isLastSearchPage,
  doesSearchIndexExist,
  isRecommendationsSelector,
} from '../selectors/search';
import {
  createSuccessActionType,
  createErrorActionType,
} from '../utils/actions';
import { flattenRequestParams } from '../utils/json-api';
import { cleanEmptyFilters } from '../utils/search';
import { storeSmartURL } from '../utils/smarturl';
import { setContainerLoadState } from '../actions/container-load-state';
import { containerLoadStateSelector } from '../selectors/container-load-state';

/**
 * Hydrate function to search page
 */
export function* hydrateSearchView() {
  const { loaded } = yield select(containerLoadStateSelector('Search'));
  if (loaded) {
    return;
  }
  // wait Next page request or location change to reperform request
  let facetHandlerTask;
  let pullupTask;
  const task = yield takeEvery([REQUEST_NEXT_SEARCH_PAGE, '@@router/LOCATION_CHANGE'], function* (action) {
    const currentPage = yield select(currentPageFromSearchIndexSelector);
    // prevent reperform request on LOCATION CHANGE for next page
    if (action.type === '@@router/LOCATION_CHANGE') {
      const pathname = action?.payload?.location?.pathname;
      if (pathname !== Routes.search) {
        yield cancel(task);
        yield cancel(facetHandlerTask);
        yield cancel(pullupTask);
        yield put(setSearchLoadedState(false));
        return;
      }
      const searchParams = action?.payload?.search || '?';
      const page = qs.parse(searchParams.substr(1))?.page;
      if (page && +page <= currentPage) {
        return;
      }
    }
    yield call(performSearch, action.type === REQUEST_NEXT_SEARCH_PAGE ? currentPage + 1 : undefined, 12);
  });
  yield put(setContainerLoadState('Search', { loaded: true }));
  // Perform the first time search request for current search
  yield call(performSearch);
  // Handle facet form change
  // We need to wait for initial search results to finish loading
  // Because an INITIALIZE action is dispatched both when the redux form finished initializing AND when the form is reset by the user
  facetHandlerTask = yield takeEvery(
    RUN_SEARCH_BY_FILTER,
    handleFacetsSelection
  );
  // inform backend for noresult found
  pullupTask = yield takeEvery(
    PULLUP_NOT_FOUND_INFORMATION,
    pullUpNoResultInformation,
  );
}

/**
 * Handle facets parsing and push new route
 */
function* handleFacetsSelection({ filtersValues }) {
  // eslint-disable-next-line no-unused-vars
  let { q, filters, ...specialsUrlParams } = yield select(urlSearchSelector);

  // filter empty facets
  filters = cleanEmptyFilters({
    ...filters,
    ...filtersValues,
  });

  // prevent change url unexpectedly
  // for exemple 2 subsequent blur
  const currentUrlSearch = yield select(urlSearchFromState);
  const newUrlSearchParams = flattenRequestParams({
    q,
    filters,
    ...specialsUrlParams,
  });
  if (currentUrlSearch && currentUrlSearch.substr(1) === qs.stringify(newUrlSearchParams)) {
    return;
  }

  // prevent from having both q & filters[tag_names] param
  if (Array.isArray(filters.tag_names) && filters.tag_names.length > 0) {
    delete newUrlSearchParams.q;
  }

  yield put(push(`${Routes.search}?${qs.stringify(newUrlSearchParams)}`));
}

/**
 * Retrieve all needed data to perform search on server
 * @param {Number} page - page to load
 * @param {Number} size - number items to load
 */
export function* performSearch(page, size = 12) {
  let searchParams = yield select(urlSearchSelector);
  const isRecommendations = yield select(isRecommendationsSelector);

  // To be compatible with old links gived to clients,
  // we need to redirect them to new (old :p) system
  if (searchParams.query) {
    searchParams.q = searchParams.query;
    delete searchParams.query;
    return !(yield call(replaceCurrentSearch, searchParams));
  }
  if (searchParams.topSearch) {
    searchParams.filters = {
      ...searchParams.filters,
      tag_names: searchParams.topSearch,
    };
    delete searchParams.topSearch;
    return !(yield call(replaceCurrentSearch, searchParams));
  }
  // Redirect ?filters[companies][MyJobGlasses]=true to ?filters[companies]=MyJobGlasses
  const oldFilterCompanies = searchParams?.filters?.companies;
  if (oldFilterCompanies && typeof oldFilterCompanies === 'object') {
    searchParams = {
      ...searchParams,
      filters: {
        ...searchParams.filters,
        companies: Object.keys(oldFilterCompanies).join(','),
        professional_types: 'company/employee',
      },
    };
    return !(yield call(replaceCurrentSearch, searchParams));
  }
  // prevent bad radius parameter
  // In the future, we can add here a system which will allow to change this parameter as bonus
  const radiusValue = searchParams?.filters?.radius;
  if (radiusValue && radiusValue !== '50km') {
    set(searchParams, 'filters.radius', '50km');
    return !(yield call(replaceCurrentSearch, searchParams));
  }

  // prevent request more page than exist
  const isLastPage = yield select(isLastSearchPage);
  if (isLastPage) {
    return;
  }

  // Calculate current page if not specified
  let realPage = page;
  if (!page) {
    page = 1;
    realPage = 1;
    if (searchParams.page) {
      realPage = +searchParams.page;
      size = +searchParams.page * (searchParams.size || size);
    }
  }

  // ensure new search to scroll to top
  if (realPage < 2) {
    window.scrollTo(0,0);
  }

  // CACHE SYSTEM : prevent do request already exist
  const indexExist = yield select(doesSearchIndexExist);
  const currentPage = yield select(currentPageFromSearchIndexSelector);
  if (indexExist && (currentPage >= realPage)) {
    return;
  }

  // destroy page/size from params to prevent pass it as it
  delete searchParams.page;
  delete searchParams.size;

  // Calculate hash for search Index
  const searchHash = yield select(searchUrlHashSelector);

  // set index as loading
  yield put(setIndexLoadingState(searchHash, true));

  // Track current search (back search to ahoy)
  yield put(trackSearch({ ...searchParams, page, size }));

  // perform search with facets
  yield put(search({ ...searchParams, page, size }));
  if (!isRecommendations) {
    yield put(getFacets(searchParams));
  }
  yield put({ type: PERFORM_SEARCH });

  const [{ success, error }, { successFacets, errorFacets }] = yield all([
    race({
      success: take(createSuccessActionType('READ_LIST', ApiResource.PROFILE)),
      error: take(createErrorActionType('READ_LIST', ApiResource.PROFILE)),
    }),
    !isRecommendations && race({
      successFacets: take(createSuccessActionType('READ', ApiResource.FACETS)),
      errorFacets: take(createErrorActionType('READ', ApiResource.FACETS)),
    }),
  ]);

  // On success case, we build index to be powerful
  if (success) {
    const professionalEmployees = success?.data?.professionalEmployee || {};
    const professionalMentors = success?.data?.professionalMentor || {};
    const keys = [
      ...Object.keys(professionalEmployees),
      ...Object.keys(professionalMentors),
    ];
    const metas = Object.values(success?.data?.meta || {});
    const pageInfo = {
      currentPage: +realPage,
      total: metas?.[0]?.meta?.stats?.total?.count || 0,
      numberOfPages: Infinity,
    };
    if (!isEmpty(keys)) {
      yield put(addToIndex(searchHash, keys, pageInfo));
      if (+realPage > 1) {
        const rawSearchParams = yield select(urlSearchSelector);
        yield put(push(`${Routes.search}?${qs.stringify({
          ...flattenRequestParams(rawSearchParams),
          page: realPage,
          size: 12,
        })}`));
      }
      if (keys.length < (success?.requestData?.params || [])['page[size]']) {
        yield put(forceLastPage(searchHash));
      }
    } else {
      yield put(forceLastPage(searchHash));
    }
  // On error case, we need to save error to be able to purpose retry query
  } else if (error) {
    yield put(setIndexError(searchHash, error));
  }

  // On facets success, save it to a specific index
  if (successFacets) {
    yield put(setSearchFacetsIndex(searchHash, successFacets?.data));
  } else if (errorFacets) {
    // @todo handle facets errors
  }
  yield put(setIndexLoadingState(searchHash, false));
}

/**
 * Replace properly url
 * @param {Object} searchParams
 * @param {Object} specialsUrlParams
 */
function* replaceCurrentSearch(searchParams, specialsUrlParams) {
  yield put(replace(`${Routes.search}?${qs.stringify(flattenRequestParams({ ...searchParams, ...specialsUrlParams }))}`));
  // We need to trigger manually this event here 🤯
  yield put({
    type: '@@router/LOCATION_CHANGE',
    payload: {
      location: {
        pathname: Routes.search,
        search: `?${qs.stringify(flattenRequestParams(searchParams))}`,
        hash: '',
      },
    },
  });
  return true;
}

/**
 * Do you think about what do this code ?
 * I don't know BUT I let this here to let you find why this exist
 * I guess, this code is to set a context for signup
 */
export const trackSearchView = ({pathname}) => {
  // Store any search that isn't the home search
  if (pathname !== Routes.search) {
    storeSmartURL({
      type: 'student',
      context: VIEWING_SEARCH_RESULTS_CONTEXT,
      pathname,
    });
  }
};

/**
 * Inform backend from no results
 */
function* pullUpNoResultInformation() {
  const params = yield select(urlSearchSelector);
  yield put(reportActionToAhoy(
    'No professional after professional search',
    params,
  ));
}
